import * as icons from '@mui/icons-material';
import { Button, Chip, IconButton, Tooltip } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid';
import { EditButton, List, useDataGrid } from '@refinedev/mui';
import React from 'react';
import { useNavigate } from 'react-router';
import { genDocumentSlug } from 'shiver-core/lib';
import { DOCUMENT_TYPES, REGIONS, type DocumentTypeKey } from 'shiver-core/static';
import { DataGrid, type GridColDef } from '../../../components/DataGrid.tsx';
import { getAppBaseUrl } from '../../../helpers/appUrl.ts';
import { useColumnVisibilityModel } from '../../../hooks/columnVisibilityModel.ts';
import { DocumentBase } from '../../../types/index.ts';

export const DocumentList = () => {
  const navigate = useNavigate();
  const { dataGridProps, filters, setFilters } = useDataGrid<DocumentBase>({
    resource: 'contents/documents',
    queryOptions: {
      select({ data, total }) {
        return {
          data: data.map((row) => DocumentBase.parse(row)),
          total,
        };
      },
    },
  });

  const columns = React.useMemo<Array<GridColDef<DocumentBase>>>(
    () =>
      [
        {
          type: 'actions',
          field: 'actions',
          headerName: '',
          maxWidth: 120,
          renderCell({ row }) {
            const previewUrl = `${getAppBaseUrl()}/document/${genDocumentSlug(row.id)}`;
            return (
              <>
                <EditButton hideText recordItemId={row.id} />
                <Tooltip title="プレビュー">
                  <IconButton
                    size="small"
                    onClick={() => {
                      window.open(previewUrl, '_blank', 'noopener,noreferrer');
                    }}
                  >
                    <icons.Visibility fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="複製">
                  <IconButton
                    size="small"
                    onClick={() => {
                      navigate(`/contents/documents/create?clone=${row.id}`);
                    }}
                  >
                    <icons.ContentCopy fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            );
          },
        },
        {
          field: 'slug',
          headerName: 'slug',
          display: 'flex',
          minWidth: 200,
          valueGetter(_, row) {
            return genDocumentSlug(row.id);
          },
        },
        {
          type: 'date',
          field: 'publishedAt',
          headerName: '公開日時',
          display: 'flex',
          minWidth: 150,
          valueFormatter(_, { publishedAt }) {
            if (!publishedAt) return '-';
            return publishedAt.toLocaleString('ja-JP').replace(/:\d{2}$/, '');
          },
        },
        {
          type: 'singleSelect',
          field: 'type',
          headerName: '記事タイプ',
          display: 'flex',
          align: 'center',
          headerAlign: 'center',
          valueOptions: (Object.keys(DOCUMENT_TYPES) as DocumentTypeKey[]).sort(
            (a, b) => DOCUMENT_TYPES[a].order - DOCUMENT_TYPES[b].order,
          ),
          getOptionLabel: (key) => {
            if (!key) return '';
            return DOCUMENT_TYPES[key as keyof typeof DOCUMENT_TYPES].name;
          },
          renderCell({ formattedValue, tabIndex }) {
            return (
              <Chip label={formattedValue} variant="outlined" size="small" tabIndex={tabIndex} />
            );
          },
        },
        {
          type: 'singleSelect',
          field: 'regions',
          headerName: '地域',
          minWidth: 200,
          sortable: false,
          valueOptions: Object.keys(REGIONS),
          getOptionLabel(key) {
            if (typeof key !== 'string') return '';
            return REGIONS[key as keyof typeof REGIONS].name;
          },
          valueGetter(_, row) {
            return row.regions;
          },
          renderCell({ tabIndex, row }) {
            return (
              <div style={{ overflow: 'auto' }}>
                {row.regions.map((item) => (
                  <Chip
                    key={item}
                    label={REGIONS[item].name}
                    variant="outlined"
                    size="small"
                    tabIndex={tabIndex}
                  />
                ))}
              </div>
            );
          },
        },
        {
          field: 'title',
          headerName: 'タイトル',
          minWidth: 400,
          flex: 1,
        },
        {
          field: 'lede',
          headerName: 'リード文',
          minWidth: 400,
          flex: 1,
          filterable: false,
          sortable: false,
          renderCell({ value }) {
            return <span style={{ whiteSpace: 'pre-line' }}>{value}</span>;
          },
        },
        {
          field: 'textHtml',
          headerName: '本文',
          minWidth: 400,
          flex: 1,
          filterable: false,
          sortable: false,
          renderCell({ value }) {
            return <span style={{ whiteSpace: 'pre-line' }}>{value}</span>;
          },
        },
        {
          field: 'authors',
          headerName: '執筆者',
          minWidth: 200,
          sortable: false,
          valueGetter(_, row) {
            return row.authors.map((item) => item.name) ?? [];
          },
          renderCell({ tabIndex, row }) {
            return (
              <div style={{ overflow: 'auto' }}>
                {row.authors.map((item) => (
                  <Chip
                    key={item.id}
                    label={item.name}
                    variant="outlined"
                    size="small"
                    tabIndex={tabIndex}
                  />
                ))}
              </div>
            );
          },
        },
        {
          field: 'attachments',
          headerName: '関連資料',
          minWidth: 200,
          sortable: false,
          valueGetter(_, row) {
            return row.attachments.map((item) => [item.title, item.url]) ?? [];
          },
          renderCell({ tabIndex, row }) {
            return (
              <div style={{ overflow: 'auto' }}>
                {row.attachments.map((item) => (
                  <Chip
                    key={item.id}
                    label={item.title}
                    variant="outlined"
                    size="small"
                    tabIndex={tabIndex}
                  />
                ))}
              </div>
            );
          },
        },
        {
          field: 'sectors',
          headerName: '分野',
          minWidth: 200,
          sortable: false,
          valueGetter(_, row) {
            return row.sectors.map((item) => item.name) ?? [];
          },
          renderCell({ tabIndex, row }) {
            return (
              <div style={{ overflow: 'auto' }}>
                {row.sectors.map((item) => (
                  <Chip
                    key={item.id}
                    label={item.name}
                    variant="outlined"
                    size="small"
                    tabIndex={tabIndex}
                  />
                ))}
              </div>
            );
          },
        },
        {
          field: 'topics',
          headerName: 'トピック',
          minWidth: 200,
          sortable: false,
          valueGetter(_, row) {
            return row.topics.map((item) => item.name) ?? [];
          },
          renderCell({ tabIndex, row }) {
            return (
              <div style={{ overflow: 'auto' }}>
                {row.topics.map((item) => (
                  <Chip
                    key={item.id}
                    label={item.name}
                    variant="outlined"
                    size="small"
                    tabIndex={tabIndex}
                  />
                ))}
              </div>
            );
          },
        },
        {
          field: 'series',
          headerName: 'シリーズ',
          minWidth: 200,
          sortable: false,
          valueGetter(_, row) {
            return row.series.map((item) => item.name) ?? [];
          },
          renderCell({ tabIndex, row }) {
            return (
              <div style={{ overflow: 'auto' }}>
                {row.series.map((item) => (
                  <Chip
                    key={item.id}
                    label={item.name}
                    variant="outlined"
                    size="small"
                    tabIndex={tabIndex}
                  />
                ))}
              </div>
            );
          },
        },
        {
          field: 'timelines',
          headerName: 'タイムライン',
          minWidth: 200,
          sortable: false,
          valueGetter(_, row) {
            return row.timelines.map((item) => item.name) ?? [];
          },
          renderCell({ tabIndex, row }) {
            return (
              <div style={{ overflow: 'auto' }}>
                {row.timelines.map((item) => (
                  <Chip
                    key={item.id}
                    label={item.name}
                    variant="outlined"
                    size="small"
                    tabIndex={tabIndex}
                  />
                ))}
              </div>
            );
          },
        },
        {
          field: 'relatedDocuments',
          headerName: '関連記事',
          minWidth: 200,
          sortable: false,
          valueGetter(_, row) {
            return row.relatedDocuments.map((item) => item.title) ?? [];
          },
          renderCell({ tabIndex, row }) {
            return (
              <div style={{ overflow: 'auto' }}>
                {row.relatedDocuments.map((item) => (
                  <Chip
                    key={item.id}
                    label={item.title}
                    variant="outlined"
                    size="small"
                    tabIndex={tabIndex}
                  />
                ))}
              </div>
            );
          },
        },
        {
          type: 'boolean',
          field: 'isSpecialOpen',
          headerName: '特別一般公開',
          display: 'flex',
          align: 'center',
        },
        {
          type: 'dateTime',
          field: 'updatedAt',
          headerName: '更新日時',
          display: 'flex',
          minWidth: 150,
          valueFormatter(_, { updatedAt }) {
            if (!updatedAt) return '-';
            return updatedAt.toLocaleString('ja-JP').replace(/:\d{2}$/, '');
          },
        },
        {
          type: 'dateTime',
          field: 'createdAt',
          headerName: '作成日時',
          display: 'flex',
          minWidth: 150,
          valueFormatter(_, { createdAt }) {
            if (!createdAt) return '-';
            return createdAt.toLocaleString('ja-JP').replace(/:\d{2}$/, '');
          },
        },
        {
          field: 'createdBy',
          headerName: '作成者',
          display: 'flex',
          minWidth: 150,
          valueGetter(_, row) {
            return row.createdBy ?? '-';
          },
        },
        {
          type: 'dateTime',
          field: 'modifiedAt',
          headerName: '変更日時',
          display: 'flex',
          minWidth: 150,
          valueFormatter(_, { modifiedAt }) {
            if (!modifiedAt) return '-';
            return modifiedAt.toLocaleString('ja-JP').replace(/:\d{2}$/, '');
          },
        },
        {
          field: 'modifiedBy',
          headerName: '変更者',
          display: 'flex',
          minWidth: 150,
          valueGetter(_, row) {
            return row.modifiedBy ?? '-';
          },
        },
      ] satisfies Array<GridColDef<DocumentBase>>,
    [navigate],
  );

  const [columnVisibilityModel, setColumnVisibilityModel] = useColumnVisibilityModel(
    'contents/documents',
    {
      lede: false,
      textHtml: false,
    },
  );

  const apiRef = useGridApiRef();

  return (
    <List
      headerButtons={({ defaultButtons }) => (
        <>
          <Button
            startIcon={<icons.IosShareOutlined />}
            onClick={() => {
              apiRef.current.exportDataAsCsv({
                fileName: `documents_${new Date().toLocaleDateString('ja-JP').replaceAll('/', '')}`,
                allColumns: true,
              });
            }}
          >
            Export CSV
          </Button>
          {defaultButtons}
        </>
      )}
    >
      <DataGrid
        {...dataGridProps}
        apiRef={apiRef}
        columns={columns}
        filters={filters}
        setFilters={setFilters}
        filterDebounceMs={800}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={setColumnVisibilityModel}
        defaultFilterColumn="title"
      />
    </List>
  );
};
